import { format, parseISO } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarClock } from '@fortawesome/pro-regular-svg-icons';
import { faAngleRight } from '@fortawesome/pro-solid-svg-icons';
import Currency from '@v2/shared/components/Currency';
import TextButton from '@v2/shared/components/TextButton';
import { useSite } from '@v2/shared/lib/site';
import Box, { BoxLabel } from './Box';
import { AutoRenewalDetail, Props } from './types';

type NextBillDateProps = Pick<Props, 'autoRenewServiceName'> & {
  autoRenewal: AutoRenewalDetail;
  onDisable: () => void;
  onDelay: () => void;
};

/**
 * When the next charge is due, and the two things a customer can do about it.
 *
 * The buttons open flows the page owns rather than modals mounted here — see Details for
 * why that has to be the case.
 *
 * A charge date in the past is not shown: on a subscription that needs attention it is
 * the date a charge *failed*, and printing it under "next bill date" would say the
 * opposite of what happened. Compared as yyyyMMdd strings, as at the source, which
 * compares the days rather than the instants — a charge due later today is still due.
 */
export default function NextBillDate({
  autoRenewal,
  autoRenewServiceName,
  onDisable,
  onDelay,
}: NextBillDateProps) {
  const site = useSite();

  const chargeDate = autoRenewal.automatic_charge_date ? parseISO(autoRenewal.automatic_charge_date) : null;
  const nextBillDate =
    chargeDate && format(chargeDate, 'yyyyMMdd') >= format(new Date(), 'yyyyMMdd') ? chargeDate : null;

  const showDelayAction = autoRenewal.status === 'active';

  // Only on a subscription that is actually going to be charged, and only once the api
  // has priced that charge — `processed` is the upcoming transaction having been worked
  // out rather than merely existing.
  const upcoming = autoRenewal.upcoming_transaction;
  const upcomingPrice =
    autoRenewal.status === 'active' && upcoming?.status === 'processed' && upcoming.price != null
      ? upcoming.price
      : null;

  return (
    <>
      <BoxLabel>Next bill date</BoxLabel>
      <Box>
        <div className="font-semibold mb-2">
          <FontAwesomeIcon icon={faCalendarClock} className="mr-1.5" />
          {nextBillDate ? format(nextBillDate, 'MMMM do, yyyy') : 'None'}
          {upcomingPrice !== null && (
            <span className="inline-flex items-center ml-1">
              (<Currency value={upcomingPrice} currencyCode={site.currency} />
              <span className="text-gray-600 text-xs ml-1">+ tax</span>)
            </span>
          )}
        </div>

        {/* Themed, not `danger`: neither of these destroys anything — disabling stops a
            renewal and delaying moves it — and the red was the brand's accent rather
            than a warning. Two of the four brands reaching this page are not red. */}
        <TextButton
          color="primary"
          size="sm"
          onClick={onDisable}
          className="mr-3 md:mr-5"
          iconAfter={faAngleRight}
        >
          Disable {autoRenewServiceName}
        </TextButton>

        {showDelayAction && (
          <TextButton color="primary" size="sm" onClick={onDelay} iconAfter={faAngleRight}>
            Delay {autoRenewServiceName}
          </TextButton>
        )}
      </Box>
    </>
  );
}
